import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { paymentsApi } from '../../lib/api';
import { Smartphone, CreditCard, Loader2, CheckCircle2, ExternalLink } from 'lucide-react';

/**
 * Gateway payment dialog (Telebirr / CBE / Chapa).
 * - Shows each provider with a "Configured" / "Coming soon" / "Simulation" badge.
 * - Configured providers redirect to the provider checkout.
 * - Simulation providers (PAYMENTS_SIMULATION_MODE=true) allow a demo confirm.
 */
export default function GatewayPaymentDialog({ open, onOpenChange, propertyId, folio, onPaid }) {
  const [providers, setProviders] = useState([]);
  const [simMode, setSimMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState(null); // active simulation payment

  const balance = folio
    ? (folio.total_charges || 0) - (folio.total_payments || 0) + (folio.total_adjustments || 0)
    : 0;

  const loadProviders = useCallback(async () => {
    try {
      const res = await paymentsApi.listProviders();
      setProviders(res.data.providers || []);
      setSimMode(res.data.simulation_mode);
    } catch (e) {
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setAmount(balance > 0 ? String(Number(balance.toFixed(2))) : '');
      setSelected(null);
      setPending(null);
      loadProviders();
    }
  }, [open]);

  const handleInitiate = async () => {
    if (!selected) { toast.error('Select a payment method'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      setSubmitting(true);
      const res = await paymentsApi.initiate(propertyId, {
        provider: selected,
        amount: amt,
        folio_id: folio?.id,
        guest_email: folio?.guests?.email,
      });
      const data = res.data;
      if (data.simulation) {
        setPending(data);
        toast.info('Simulated payment created — confirm to post to folio');
      } else if (data.checkout_url) {
        toast.success('Redirecting to payment gateway…');
        window.location.href = data.checkout_url;
      } else {
        toast.success(`Payment ${data.status}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSimConfirm = async () => {
    if (!pending) return;
    try {
      setSubmitting(true);
      await paymentsApi.simulateConfirm(pending.payment_id);
      toast.success('Payment recorded on folio (simulation)');
      onOpenChange(false);
      if (onPaid) onPaid();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to confirm');
    } finally {
      setSubmitting(false);
    }
  };

  const providerIcon = (code) => (code === 'CHAPA' ? CreditCard : Smartphone);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Collect Payment via Gateway</DialogTitle>
          <DialogDescription>
            Telebirr, CBE Birr or Chapa. Balance due: <b>ETB {Number(balance).toLocaleString()}</b>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : pending ? (
          <div className="space-y-3 py-2">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
              <p className="font-medium text-amber-800">Simulation payment created</p>
              <p className="text-amber-700">Ref: {pending.tx_ref}</p>
              <p className="text-amber-700">Amount: ETB {Number(pending.amount).toLocaleString()}</p>
              <p className="text-xs text-amber-600 mt-1">No real money moves in simulation mode.</p>
            </div>
            <Button onClick={handleSimConfirm} disabled={submitting} className="w-full">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Confirm &amp; Post to Folio
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Amount (ETB)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <div className="space-y-2">
                {providers.map((p) => {
                  const Icon = providerIcon(p.code);
                  const disabled = !p.enabled;
                  return (
                    <button
                      key={p.code}
                      type="button"
                      disabled={disabled}
                      onClick={() => setSelected(p.code)}
                      data-testid={`provider-${p.code}`}
                      className={[
                        'w-full flex items-center gap-3 rounded-lg border p-3 text-left transition',
                        disabled ? 'opacity-60 cursor-not-allowed bg-slate-50' : 'hover:border-indigo-400',
                        selected === p.code ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-slate-200',
                      ].join(' ')}
                    >
                      <Icon className="h-5 w-5 text-slate-600" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{p.name}</p>
                        <p className="text-xs text-slate-500 truncate">{p.methods.join(', ')}</p>
                      </div>
                      {p.configured ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Configured</Badge>
                      ) : p.simulation ? (
                        <Badge className="bg-amber-100 text-amber-700">Simulation</Badge>
                      ) : (
                        <Badge variant="secondary">Coming soon</Badge>
                      )}
                    </button>
                  );
                })}
              </div>
              {!simMode && providers.every((p) => !p.configured) && (
                <p className="text-xs text-slate-500">
                  No gateway credentials configured yet. Add provider API keys in the backend
                  environment to enable live payments.
                </p>
              )}
            </div>
          </div>
        )}

        {!pending && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={handleInitiate} disabled={submitting || loading || !selected}>
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
              Continue
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
